import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Date utilities
export function formatDate(date: string | Date, formatStr = 'dd/MM/yyyy'): string {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return 'Data inválida';
    return format(dateObj, formatStr, { locale: ptBR });
  } catch {
    return 'Data inválida';
  }
}

export function formatDateTime(date: string | Date): string {
  return formatDate(date, 'dd/MM/yyyy HH:mm');
}

export function formatTime(date: string | Date): string {
  return formatDate(date, 'HH:mm');
}

export function formatDateForInput(date: string | Date): string {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return '';
    return format(dateObj, 'yyyy-MM-dd');
  } catch {
    return '';
  }
}

export function isDateOverdue(date: string | Date): boolean {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return false;
    return dateObj < new Date();
  } catch {
    return false;
  }
}

// Currency utilities
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

export function parseCurrency(value: string): number {
  const cleanValue = value.replace(/[^\d,]/g, '').replace(',', '.');
  return parseFloat(cleanValue) || 0;
}

// Document utilities
export function formatCNPJ(cnpj: string): string {
  const cleanCNPJ = cnpj.replace(/\D/g, '');
  return cleanCNPJ.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

export function formatPhone(phone: string): string {
  const cleanPhone = phone.replace(/\D/g, '');
  if (cleanPhone.length === 11) {
    return cleanPhone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (cleanPhone.length === 10) {
    return cleanPhone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return phone;
}

// Text utilities
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

export function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

// Array utilities
export function groupBy<T, K extends keyof T>(array: T[], key: K): Record<string, T[]> {
  return array.reduce((result, item) => {
    const group = String(item[key]);
    if (!result[group]) {
      result[group] = [];
    }
    result[group].push(item);
    return result;
  }, {} as Record<string, T[]>);
}

export function sortBy<T>(array: T[], key: keyof T, direction: 'asc' | 'desc' = 'asc'): T[] {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    
    if (aVal === bVal) return 0;
    
    const comparison = aVal < bVal ? -1 : 1;
    return direction === 'asc' ? comparison : -comparison;
  });
}

// Validation utilities
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function isValidCNPJ(cnpj: string): boolean {
  const cleanCNPJ = cnpj.replace(/\D/g, '');
  
  if (cleanCNPJ.length !== 14) return false;
  if (/^(\d)\1+$/.test(cleanCNPJ)) return false;
  
  let sum = 0;
  let weight = 2;
  
  for (let i = 11; i >= 0; i--) {
    sum += parseInt(cleanCNPJ.charAt(i)) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }
  
  let digit = sum % 11;
  digit = digit < 2 ? 0 : 11 - digit;
  
  if (parseInt(cleanCNPJ.charAt(12)) !== digit) return false;
  
  sum = 0;
  weight = 2;
  
  for (let i = 12; i >= 0; i--) {
    sum += parseInt(cleanCNPJ.charAt(i)) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }
  
  digit = sum % 11;
  digit = digit < 2 ? 0 : 11 - digit;
  
  return parseInt(cleanCNPJ.charAt(13)) === digit;
}

// Local storage utilities
export function getFromStorage<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
}

export function setToStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn('Failed to save to localStorage:', error);
  }
}

export function removeFromStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to remove from localStorage:', error);
  }
}

// URL utilities
export function createQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== null) {
      searchParams.set(key, String(value));
    }
  });
  
  return searchParams.toString();
}

export function parseQueryString(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const result: Record<string, string> = {};
  
  params.forEach((value, key) => {
    result[key] = value;
  });
  
  return result;
}

// File utilities
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function getFileExtension(filename: string): string {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
}

// Debounce utility
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// Random utilities
export function generateId(): string {
  return Math.random().toString(36).substr(2, 9);
}

export function randomColor(): string {
  const colors = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b',
    '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

// Funções para validação e formatação de telefone
export const formatPhoneNumber = (value: string): string => {
  // Remove tudo que não é dígito
  const numbers = value.replace(/\D/g, '');
  
  // Aplica máscara baseada no número de dígitos
  if (numbers.length <= 2) {
    return `+${numbers}`;
  } else if (numbers.length <= 4) {
    return `+${numbers.slice(0, 2)} (${numbers.slice(2)}`;
  } else if (numbers.length <= 8) {
    return `+${numbers.slice(0, 2)} (${numbers.slice(2, 4)}) ${numbers.slice(4)}`;
  } else if (numbers.length <= 12) {
    return `+${numbers.slice(0, 2)} (${numbers.slice(2, 4)}) ${numbers.slice(4, 8)}-${numbers.slice(8)}`;
  } else if (numbers.length <= 13) {
    // Para números brasileiros completos (13 dígitos)
    return `+${numbers.slice(0, 2)} (${numbers.slice(2, 4)}) ${numbers.slice(4, 9)}-${numbers.slice(9)}`;
  } else {
    // Limita a 13 dígitos para números brasileiros
    const limitedNumbers = numbers.slice(0, 13);
    return `+${limitedNumbers.slice(0, 2)} (${limitedNumbers.slice(2, 4)}) ${limitedNumbers.slice(4, 9)}-${limitedNumbers.slice(9)}`;
  }
};

export const validatePhoneNumber = (phone: string): { isValid: boolean; error?: string } => {
  // Remove formatação
  const cleanPhone = phone.replace(/\D/g, '');
  
  // Validações básicas
  if (!cleanPhone) {
    return { isValid: false, error: 'Número de telefone é obrigatório' };
  }
  
  if (cleanPhone.length < 10) {
    return { isValid: false, error: 'Número deve ter pelo menos 10 dígitos' };
  }
  
  if (cleanPhone.length > 15) {
    return { isValid: false, error: 'Número deve ter no máximo 15 dígitos' };
  }
  
  // Validação específica para Brasil (se começar com 55)
  if (cleanPhone.startsWith('55')) {
    if (cleanPhone.length !== 13) {
      return { isValid: false, error: 'Número brasileiro deve ter 13 dígitos (código do país + DDD + número)' };
    }
    
    const ddd = cleanPhone.slice(2, 4);
    const number = cleanPhone.slice(4);
    
    // Validar DDD brasileiro
    const validDDDs = [
      '11', '12', '13', '14', '15', '16', '17', '18', '19', '21', '22', '24', '27', '28',
      '31', '32', '33', '34', '35', '37', '38', '41', '42', '43', '44', '45', '46', '47',
      '48', '49', '51', '53', '54', '55', '61', '62', '63', '64', '65', '66', '67', '68',
      '69', '71', '73', '74', '75', '77', '79', '81', '82', '83', '84', '85', '86', '87',
      '88', '89', '91', '92', '93', '94', '95', '96', '97', '98', '99'
    ];
    
    if (!validDDDs.includes(ddd)) {
      return { isValid: false, error: 'DDD inválido' };
    }
    
    // Validar formato do número brasileiro
    if (!/^[9]\d{8}$/.test(number)) {
      return { isValid: false, error: 'Número deve começar com 9 e ter 9 dígitos (ex: 98816-2000)' };
    }
  }
  
  return { isValid: true };
};

export const cleanPhoneNumber = (phone: string): string => {
  return phone.replace(/\D/g, '');
};

export const formatPhoneForWhatsApp = (phone: string): string => {
  const clean = cleanPhoneNumber(phone);
  
  // Se não tem código do país, assume Brasil
  if (clean.length === 11) {
    return `55${clean}`;
  }
  
  return clean;
};

/**
 * File URL utilities
 * 
 * Versão: 2.0.1
 * Data: 2025-01-XX
 * 
 * Função utilitária para construir URLs completas de arquivos (comprovantes, imagens, etc.)
 * Resolve URLs relativas para URLs absolutas usando a configuração da API.
 * 
 * @param fileUrl - URL relativa ou absoluta do arquivo
 * @returns URL completa do arquivo
 * 
 * Exemplos:
 * - '/api/v1/upload/comprovantes/public/file.jpg' -> 'http://api:3000/api/v1/upload/comprovantes/public/file.jpg'
 * - '/uploads/comprovantes/file.jpg' -> 'http://localhost:5173/uploads/comprovantes/file.jpg'
 * - 'http://example.com/file.jpg' -> 'http://example.com/file.jpg' (mantém como está)
 */
export function getFileUrl(fileUrl: string): string {
  // Se já é uma URL completa, retornar como está
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    return fileUrl;
  }
  
  // Construir URL completa usando a base da API
  const apiBaseUrl = (import.meta as any).env?.VITE_API_URL || window.location.origin;
  
  // Se a URL já começa com /api/v1, usar a base da API
  if (fileUrl.startsWith('/api/v1')) {
    return `${apiBaseUrl}${fileUrl}`;
  }
  
  // Se começa com /uploads, usar window.location.origin
  if (fileUrl.startsWith('/uploads')) {
    return `${window.location.origin}${fileUrl}`;
  }
  
  // Caso padrão: adicionar /api/v1 se necessário
  return `${apiBaseUrl}/api/v1${fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`}`;
}
